'use client'
import React, { PropsWithChildren } from 'react'
import { CloseCross } from '../icons'
import ButtonAdminComponent, { ButtonStyle } from './button-admin'
import clsx from 'clsx'
import * as DialogPrimitive from '@radix-ui/react-dialog'

export type ModalGenericProps = PropsWithChildren & {
  onCloseModal: () => void
  isLoading: boolean
  isButtonDisabled?: boolean
  onSubmit: () => void
  onSubmitSecondary?: () => void
  icon?: React.ReactElement
  title: string | React.ReactElement
  buttonContent?: string
  secondaryButtonContent?: string
  trigger?: React.ReactNode
  className?: string
  handleOpenChange?: (val: boolean) => void
  isOpen?: boolean
  buttonStyle?: ButtonStyle
  zIndex?: number
}

export function ModalGeneric({
  handleOpenChange,
  isOpen,
  trigger,
  onCloseModal,
  isButtonDisabled = false,
  isLoading,
  onSubmit,
  icon,
  title,
  buttonContent,
  className,
  secondaryButtonContent,
  onSubmitSecondary,
  buttonStyle,
  zIndex = 50,
  children,
}: ModalGenericProps) {
  const overlayStyle = { zIndex }
  const contentStyle = { zIndex }

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Close asChild>
          <DialogPrimitive.Overlay
            className="pointer-events-auto fixed inset-0 h-screen w-screen origin-bottom animate-modal-overlay bg-[rgba(0,0,0,0.4)] bp1:transform-none bp1:animate-none bp1:opacity-100"
            style={overlayStyle}
          />
        </DialogPrimitive.Close>
        <div
          className="fixed inset-0 flex items-start justify-center overflow-y-auto pt-[5vh] bp2:pt-[2vh] bp1:pt-0"
          style={overlayStyle}
        >
          <DialogPrimitive.Content
            className={clsx(
              'w-[640px] animate-modal-content overflow-hidden rounded-xl border border-[rgba(0,0,0,0.1)] bg-white focus:outline-none flex flex-col max-h-[90vh] mb-[5vh]',
              'bp2:max-w-[90%]',
              'bp1:!w-full bp1:!max-w-full bp1:!max-h-full bp1:!h-full bp1:!rounded-none bp1:!border-0 bp1:!mb-0',
              className
            )}
            style={contentStyle}
            aria-describedby={undefined}
          >
          <div className={'flex flex-col h-full min-h-0 overflow-hidden'}>
            {/* Header fijo con título y botón cerrar */}
            <div className="flex-shrink-0 flex w-full justify-between items-center p-4 border-b border-gray-100 bg-white">
              <DialogPrimitive.Title className="text-[16px] font-semibold tracking-[-0.1px] text-black">
                {title}
              </DialogPrimitive.Title>
              <div
                className="cursor-pointer p-2 -mr-2"
                onClick={async () => {
                  onCloseModal()
                }}>
                <CloseCross className="fill-black stroke-black" />
              </div>
            </div>

            {/* Contenido scrollable */}
            <div className="flex-1 overflow-y-auto min-h-0 p-4">
              {icon && <div className={'m-auto flex pb-12'}>{icon}</div>}
              <div className="flex flex-col gap-5 text-[14px] font-normal text-[#0E0E0E]">
                {children}
              </div>
            </div>

            {/* Footer fijo con botones */}
            {(buttonContent || secondaryButtonContent) && (
              <div className="flex-shrink-0 flex h-fit w-full items-center justify-end gap-4 border-t border-[#EFEFEF] p-4 bg-white">
                <div className={'flex flex-row gap-2'}>
                  {secondaryButtonContent && (
                    <p
                      className={
                        'my-auto cursor-pointer p-2 text-[16px] font-medium opacity-60'
                      }
                      onClick={onSubmitSecondary}>
                      {secondaryButtonContent}
                    </p>
                  )}

                  {buttonContent && (
                    <ButtonAdminComponent
                      text={buttonContent}
                      onClick={onSubmit}
                      loading={isLoading}
                      disabled={isButtonDisabled}
                      className={'w-fit'}
                      style={buttonStyle}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
